import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'i18n'
import CopyToClipboardButton from './CopyToClipboardButton'
import analytics from 'global/AnalyticsData'

import './ResultPanel.scss'
import { useMemo } from 'react'
import OptionalDropdownButton from 'steps/tips-and-tricks/components/OptionalDropdownButton'
import GlobalATC from './GlobalATC'
import { useWindowSize } from 'hooks/useWindowSize'
import { formatStepImageUrl } from 'util/beaker2-step-image-url'

const validateResponseFormat = response => {
  try {
    const requiredKeys = [
      'title',
      'what_to_do',
      'why_it_works',
      'what_you_need',
    ]
    // Check if response is an object and has all required keys
    const hasAllKeys = Object.values(
      response.map(item => requiredKeys.every(key => key in Object.keys(item))),
    ).includes(false)
    console.log('Has all required keys:', hasAllKeys)

    if (typeof response === 'object' && hasAllKeys) {
      return true
    } else {
      console.warn('Missing keys in response:', response)
    }
  } catch (error) {
    console.warn('Response validation failed:', error)
  }

  return false
}

export const ResultPanel = ({
  result = [],
  state,
  step,
  atc,
  globalAtc,
  resultCopyAllToClipboard,
  imageLibrary,
}) => {
  const { t } = useTranslation('tipsAndTricks')
  const [isGenerating, setIsGenerating] = useState(true)
  const [isValidRecipe, setIsValidRecipe] = useState(false)
  const [images, setImages] = useState([])

  const { innerWidth: screenWidth } = useWindowSize()

  useEffect(() => {
    if (Object.keys(result).length > 0) {
      const valid = validateResponseFormat(result)
      setIsValidRecipe(valid)
      setIsGenerating(false)

      let newImages = Array(result.length).fill(1)
      let library = imageLibrary

      if (library && library.length > 0) {
        // Find matching images
        result.forEach((resultItem, index) => {
          let matchFound = false
          let targetText = Object.values(resultItem).join(' ').toLowerCase()

          library.forEach((image, i) => {
            if (matchFound) return
            let mactched = image.keywords?.some(keyword =>
              targetText.includes(keyword),
            )
            if (mactched) {
              matchFound = true
              newImages[index] = image
              library.splice(i, 1)
            }
          })
        })

        // Assign backup images
        library = library.filter(image => image.isBackup)
        result.forEach((resultItem, index) => {
          if (newImages[index] === 1) {
            newImages[index] = library.splice(0, 1)[0]
          }
        })

        setImages(newImages)
      }
    }
  }, [result])

  const handleResultLinkClick = async (id, text, url) => {
    analytics.stepEvent('step_button_click', step, {
      result_id: id,
      result: text + ' | ' + url,
    })
  }

  const firstKeyItemShown = useCallback(
    resultItem => {
      try {
        if (!atc) {
          return null
        }

        let firstKeyItem = null
        let targetText = Object.values(resultItem).join(' ').toLowerCase()
        atc.forEach(item => {
          if (firstKeyItem) return
          const foundKeyItem = targetText.includes(item.keyword.toLowerCase())

          if (foundKeyItem) {
            firstKeyItem = item
          }
        })

        return firstKeyItem || null
      } catch (error) {
        console.error('Error finding first key item:', error)
        return null
      }
    },
    [atc],
  )

  const handleCopyAllOnClick = () => {
    const text = result.map((item, index) => {
      return (
        index +
        1 +
        '. ' +
        item.title +
        '\n' +
        t('whatToDo') +
        ' ' +
        item.what_to_do +
        '\n' +
        t('whyItWorks') +
        ' ' +
        item.why_it_works +
        '\n' +
        t('whatYouNeed') +
        ' ' +
        item.what_you_need +
        '\n' +
        '\n'
      )
    })
    navigator.clipboard.writeText(text.reduce((prev, cur) => prev + cur))
    analytics.stepEvent('step_interaction_genai_result_copy', step)
  }

  const handleCopyOnClick = item => {
    const text =
      item.title +
      '\n' +
      t('whatToDo') +
      ' ' +
      item.what_to_do +
      '\n' +
      t('whyItWorks') +
      ' ' +
      item.why_it_works +
      '\n' +
      t('whatYouNeed') +
      ' ' +
      item.what_you_need +
      '\n' +
      '\n'

    navigator.clipboard.writeText(text)
    analytics.stepEvent('step_interaction_genai_result_copy', step)
  }

  const CursorSvg = () => {
    return (
      <svg
        viewBox="8 4 8 16"
        xmlns="http://www.w3.org/2000/svg"
        className="cursor"
      >
        <rect x="12" y="0" width="4" height="30" fill="#ffffff" />
      </svg>
    )
  }

  const atcDisplay = (item, index, imageClassName, buttonClassName) => {
    var imageUrl = item && formatStepImageUrl(item.itemImageId)
    return (
      <div className="atcDisplay">
        <img className={imageClassName} alt={''} src={imageUrl} />
        <div>
          {item.dropdownButtons.map(dropdownButton => (
            <OptionalDropdownButton
              key={index}
              id={dropdownButton.id}
              label={dropdownButton.label}
              links={dropdownButton.links}
              buttonClassName={buttonClassName}
              onLinkClick={handleResultLinkClick}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={'ResultPanel-tips-and-tricks'}>
      {isGenerating ? (
        <div className="typing-card">
          <span dangerouslySetInnerHTML={{ __html: state.text }} />
          <CursorSvg />
        </div>
      ) : (
        <div>
          {globalAtc &&
            result.every(item => firstKeyItemShown(item) === null) && (
              <>
                <div className="globalAtc-title">{globalAtc.title}</div>
                {globalAtc.products.length > 3 || screenWidth < 670 ? (
                  <div className="globalAtc-carousel">
                    <GlobalATC
                      chidrens={globalAtc.products.map((item, index) => (
                        <div>
                          {atcDisplay(
                            item,
                            index,
                            'img-atc-global',
                            'atc-button-global',
                          )}
                        </div>
                      ))}
                    />
                  </div>
                ) : (
                  <div className="glovalAtc-product">
                    {globalAtc.products.map((item, index) => (
                      <div>
                        {atcDisplay(
                          item,
                          index,
                          'img-atc-global',
                          'atc-button-global',
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          {result.map((item, index) => {
            let image = images && images.length > 0 && images[index]
            let keyItem = firstKeyItemShown(item)

            let imageUrl = image && formatStepImageUrl(image.itemImageId)
            return (
              <div>
                {index > 0 && <div className="item-divider" />}
                <div className="item-title">
                  {index + 1 + '. ' + item.title}
                </div>
                <div className="item-content">
                  <div className="content-left">
                    {imageUrl && (
                      <img className="img-top" alt={''} src={imageUrl} />
                    )}
                    {keyItem &&
                      atcDisplay(keyItem, index, 'img-atc', 'atc-button')}
                  </div>
                  <div className="content-right">
                    <div className="content-title">{t('whatToDo')}</div>
                    <div className="content-body">{item.what_to_do}</div>
                    <div className="content-title">{t('whyItWorks')}</div>
                    <div className="content-body">{item.why_it_works}</div>
                    <div className="content-title">{t('whatYouNeed')}</div>
                    <div className="content-body">
                      {item.what_you_need.split(',').map(item => (
                        <li>
                          {item.trim().charAt(0).toUpperCase() +
                            item.trim().slice(1)}
                        </li>
                      ))}
                    </div>
                  </div>
                </div>
                {resultCopyAllToClipboard === 'COPY_INDIVIDUALLY' && (
                  <div className="copy-button">
                    <CopyToClipboardButton
                      label={t('copyToClipboard')}
                      onClick={() => {
                        handleCopyOnClick(item)
                        analytics.stepEvent(
                          'step_interaction_genai_result_copy',
                          step,
                        )
                      }}
                    />
                  </div>
                )}
              </div>
            )
          })}
          {resultCopyAllToClipboard === 'COPY_ALL' && (
            <div className="copy-button">
              <CopyToClipboardButton
                label={t('copyAllToClipboard')}
                onClick={() => {
                  handleCopyAllOnClick()
                  analytics.stepEvent(
                    'step_interaction_genai_result_copy',
                    step,
                  )
                }}
              />
            </div>
          )}
        </div>
      )}
      {!isGenerating && !isValidRecipe && (
        <div className="error-message">
          <p>{t('error.result')}</p>
        </div>
      )}
    </div>
  )
}

export default ResultPanel
